import axios from "axios";
import JSZip from "jszip";
import jsPDF from "jspdf";
import { __API_URL__ } from "../SERVER_URL";
import { fetchPoemSurveyAnswers } from "../hooks/useGetSurveyAnswers";
import { getReportStatus, getReportRows } from "../hooks/useStartReportGenerationMutation";
import { buildPoemFooterText, renderPoemSurveysToPdf } from "./poemPdfUtils";

const sanitizeFilenamePart = (value) => String(value ?? "").replace(/[\\/:*?"<>|]/g, "_");

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MANIFEST_POLL_INTERVAL_MS = 1500;
const MANIFEST_ROWS_PAGE_SIZE = 500;

// The manifest itself runs as a Celery job (not a synchronous request) because for a trial with
// many visits/patients it fans out into thousands of queries -- easily blowing past a request's
// timeout. This starts the job, polls it via the same generic status endpoint every report kind
// uses, then pages through its rows once it succeeds.
const fetchPoemManifest = async ({ manifestPayload, token, signal }) => {
  const startRes = await axios.post(
    `${__API_URL__}/api/reports/start_poem_filled_pdf_manifest/`,
    manifestPayload,
    {
      headers: { Authorization: `Token ${token}` },
      signal,
      validateStatus: (s) => s === 202 || (s >= 200 && s < 300),
    }
  );

  const jobId = startRes.data?.job_id;
  if (!jobId) throw new Error("Backend did not return job_id for POEM manifest job.");

  const statusUrl = `${__API_URL__}/api/reports/${jobId}/status`;
  const rowsUrlBase = `${__API_URL__}/api/reports/${jobId}/rows`;

  let statusData;
  for (;;) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    statusData = await getReportStatus({ statusUrl, signal });
    if (statusData.status === "SUCCESS" || statusData.status === "FAILED") break;
    await wait(MANIFEST_POLL_INTERVAL_MS);
  }

  if (statusData.status === "FAILED") {
    throw new Error(statusData.error || "Failed to generate POEM manifest.");
  }

  const totalRows = statusData.total_rows || 0;
  if (totalRows === 0) return [];

  const rows = [];
  for (let offset = 0; offset < totalRows; offset += MANIFEST_ROWS_PAGE_SIZE) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const page = await getReportRows({
      rowsUrlBase,
      limit: MANIFEST_ROWS_PAGE_SIZE,
      offset,
      signal,
    });
    rows.push(...(page.results || []));
  }

  return rows;
};

const buildSinglePatientPdfBlob = ({ trialName, siteId, participantId, sessions }) => {
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

  sessions.forEach(({ visitName, inappCompletedDate, surveyList }, index) => {
    if (index > 0) pdf.addPage();
    const footerText = buildPoemFooterText({
      trialName,
      visitName,
      siteId,
      participantId,
      visitDate: inappCompletedDate,
    });
    renderPoemSurveysToPdf(pdf, { surveyList, footerText });
  });

  return pdf.output("blob");
};

const buildSingleVisitPdfBlob = ({
  trialName,
  siteId,
  participantId,
  visitName,
  inappCompletedDate,
  surveyList,
}) => {
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const footerText = buildPoemFooterText({
    trialName,
    visitName,
    siteId,
    participantId,
    visitDate: inappCompletedDate,
  });
  renderPoemSurveysToPdf(pdf, { surveyList, footerText });
  return pdf.output("blob");
};

// manifestPayload is posted to the manifest endpoint as-is -- Export Study passes just
// { trials: [{ id: trialId }] } (trial-wide, no filters), while AdHocReport passes its full
// filter payload (site_number/participant_number/visit included) so the zip only covers what's
// currently filtered on screen.
//
// signal is an AbortSignal: passed straight through to the manifest call and every per-session
// fetch, so an in-flight request is cancelled immediately, and checked at the top of every worker
// loop so a cancellation lands between units too, not just mid-request. Returns { aborted: true }
// instead of a zip if the export was cancelled partway through.
export const generateTrialPoemZip = async ({
  manifestPayload,
  token,
  individualVisits,
  onProgress,
  signal,
}) => {
  const manifest = await fetchPoemManifest({ manifestPayload, token, signal });

  if (!manifest?.length) {
    return { zipBlob: null, failedEntries: [] };
  }

  const zip = new JSZip();
  const failedEntries = [];

  // individualVisits: one unit of work per session. combined: one unit per patient, bundling
  // that patient's sessions into a single multi-visit PDF (mirrors PoemList's combined export).
  const units = individualVisits
    ? manifest.map((entry) => [entry])
    : Object.values(
        manifest.reduce((acc, entry) => {
          (acc[entry.patient_id] = acc[entry.patient_id] || []).push(entry);
          return acc;
        }, {})
      );

  onProgress?.({ current: 0, total: manifest.length });

  let completed = 0;
  const processUnit = async (entries) => {
    try {
      if (individualVisits) {
        const entry = entries[0];
        const surveyList = await fetchPoemSurveyAnswers({
          patientId: entry.patient_id,
          sessionId: entry.session_id,
          signal,
        });
        if (surveyList?.length > 0) {
          const blob = buildSingleVisitPdfBlob({
            trialName: entry.trial_name,
            siteId: entry.site_id,
            participantId: entry.participant_id,
            visitName: entry.visit_name,
            inappCompletedDate: entry.inapp_completed_date,
            surveyList,
          });
          const visitFilename = sanitizeFilenamePart(entry.visit_name);
          zip.folder(`${entry.site_id}/${entry.participant_id}`).file(`${visitFilename}.pdf`, blob);
        }
      } else {
        const sessions = await Promise.all(
          entries.map(async (entry) => ({
            visitName: entry.visit_name,
            inappCompletedDate: entry.inapp_completed_date,
            surveyList: await fetchPoemSurveyAnswers({
              patientId: entry.patient_id,
              sessionId: entry.session_id,
              signal,
            }),
          }))
        );
        const nonEmptySessions = sessions.filter((session) => session.surveyList?.length > 0);
        if (nonEmptySessions.length > 0) {
          const {
            site_id: siteId,
            participant_id: participantId,
            trial_name: trialName,
          } = entries[0];
          const blob = buildSinglePatientPdfBlob({
            trialName,
            siteId,
            participantId,
            sessions: nonEmptySessions,
          });
          zip.folder(siteId).file(`${participantId}.pdf`, blob);
        }
      }
    } catch {
      if (!signal?.aborted) {
        const entry = entries[0];
        failedEntries.push(
          individualVisits
            ? `${entry.participant_id} — ${entry.visit_name}`
            : `${entry.participant_id} (${entries.length} visit(s))`
        );
      }
    }
    completed += entries.length;
    onProgress?.({ current: completed, total: manifest.length });
  };

  // Hand-rolled concurrency-limited worker pool, matching archiveEcrfZipUtils.js: each worker
  // pulls the next unclaimed unit and awaits it before moving on, so at most DEFAULT_CONCURRENCY
  // units are in flight together -- sized for the Export Study path's platform-wide 2-slot cap.
  const DEFAULT_CONCURRENCY = 4;
  let nextIndex = 0;
  const workers = Array.from({ length: Math.min(DEFAULT_CONCURRENCY, units.length) }, async () => {
    while (nextIndex < units.length) {
      if (signal?.aborted) return;
      const entries = units[nextIndex++];
      await processUnit(entries);
    }
  });
  await Promise.all(workers);

  if (signal?.aborted) {
    return { zipBlob: null, failedEntries, aborted: true };
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  return { zipBlob, failedEntries };
};

export const uploadPoemFilledPdfZip = async ({ zipBlob, token, signal }) => {
  const presignedRes = await axios.post(
    `${__API_URL__}/api/photos/get_presigned_urls/`,
    {
      uploadType: "study_export_poem_pdf",
      files: [{ name: "poem_filled_pdfs.zip", type: "application/zip" }],
    },
    { headers: { Authorization: `Token ${token}` }, signal }
  );
  const [{ upload_url, blob_path, content_type }] = presignedRes.data.urls;

  const res = await fetch(upload_url, {
    method: "PUT",
    headers: { "Content-Type": content_type },
    body: zipBlob,
    signal,
  });
  if (!res.ok) {
    throw new Error("Failed to upload POEM PDF zip.");
  }

  return blob_path;
};
